import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header, API, Footer } from "../App";
import { useWidgetMode } from "@/lib/widgetMode";
import { brandConfig } from "@/lib/brandConfig";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, ArrowLeft, Gauge, MapPin } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const MileageZipScreen = ({ state, updateState }) => {
  const navigate = useNavigate();
  const [mileage, setMileage] = useState(state.mileage || '');
  const [zipCode, setZipCode] = useState(state.zipCode || '');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { isCompact: compact } = useWidgetMode();

  useEffect(() => {
    if (!state.vehicleInfo) {
      navigate('/');
    }
  }, [state.vehicleInfo, navigate]);

  const validate = () => {
    const newErrors = {};
    
    const mileageNum = parseInt(mileage.replace(/,/g, ''));
    if (!mileage || isNaN(mileageNum) || mileageNum < 0 || mileageNum > 500000) {
      newErrors.mileage = 'Please enter a valid mileage (0-500,000)';
    }
    
    const zipRegex = /^\d{5}$/;
    if (!zipCode || !zipRegex.test(zipCode)) {
      newErrors.zipCode = 'Please enter a valid 5-digit ZIP code';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;

    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/offers/instant`, {
        vin: state.vin,
        mileage: parseInt(mileage.replace(/,/g, '')),
        zip_code: zipCode,
        vehicle_info: state.vehicleInfo,
        partner_key: brandConfig.partnerKey
      });

      updateState({
        mileage: mileage.replace(/,/g, ''),
        zipCode,
        offer: response.data
      });
      
      navigate('/instant-offer');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to generate offer. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const formatMileage = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers ? parseInt(numbers).toLocaleString() : '';
  };

  if (!state.vehicleInfo) return null;

  const fieldHeight = compact ? "h-11" : "h-14";

  return (
    <div className={compact ? "flex flex-col bg-white" : "min-h-screen flex flex-col bg-white"}>
      <Header showProgress currentStep={2} variant="light" />

      <main className={compact ? "py-4" : "min-h-[calc(100vh-80px)] py-12 sm:py-20"}>
        <div className="container-flow">
          {/* Header */}
          <div className={`text-center opacity-0 animate-fade-in-up ${compact ? "mb-4" : "mb-10"}`}>
            <span className={`text-label text-blue-600 block ${compact ? "mb-1" : "mb-4"}`}>STEP 3 OF 7</span>
            <h1 className={compact ? "text-xl font-medium tracking-tight text-slate-900 mb-1" : "text-heading-2 mb-4"}>
              Almost There!
            </h1>
            <p className={compact ? "text-sm text-slate-500" : "text-body"}>
              Tell us a bit more about your {state.vehicleInfo.year} {state.vehicleInfo.make} {state.vehicleInfo.model}.
            </p>
          </div>

          {/* Form Card */}
          <div className={`surface-card opacity-0 animate-fade-in-up animate-stagger-2 ${compact ? "p-4" : "p-8 sm:p-12"}`}>
            <form onSubmit={handleSubmit} className={compact ? "space-y-4" : "space-y-6"}>
              {/* Mileage */}
              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">
                  Current Mileage
                </label>
                <div className="relative">
                  <Gauge className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="text"
                    data-testid="mileage-input"
                    placeholder="e.g., 45,000"
                    value={mileage}
                    onChange={(e) => {
                      setMileage(formatMileage(e.target.value));
                      setErrors(prev => ({ ...prev, mileage: null }));
                    }}
                    inputMode="numeric"
                    className={`pl-12 ${fieldHeight} text-lg rounded-xl border-slate-300 focus:border-blue-500 focus:ring-blue-500/20`}
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">
                    miles
                  </span>
                </div>
                {errors.mileage && (
                  <p className="mt-2 text-sm text-red-600" data-testid="mileage-error">{errors.mileage}</p>
                )}
              </div>

              {/* ZIP Code */}
              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">
                  ZIP Code
                </label>
                <div className="relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="text"
                    data-testid="zip-input"
                    placeholder="e.g., 90210"
                    value={zipCode}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                      setZipCode(val);
                      setErrors(prev => ({ ...prev, zipCode: null }));
                    }}
                    inputMode="numeric"
                    className={`pl-12 ${fieldHeight} text-lg rounded-xl border-slate-300 focus:border-blue-500 focus:ring-blue-500/20`}
                    maxLength={5}
                  />
                </div>
                {errors.zipCode && (
                  <p className="mt-2 text-sm text-red-600" data-testid="zip-error">{errors.zipCode}</p>
                )}
                <p className="mt-2 text-xs text-slate-500">
                  Your location helps us find comparable vehicles in your area
                </p>
              </div>

              {/* Actions */}
              <div className={`flex flex-col @sm:flex-row ${compact ? "gap-2 pt-2" : "gap-4 pt-4"}`}>
                <Button
                  type="submit"
                  disabled={loading}
                  className={`flex-1 ${fieldHeight} btn-primary`}
                  data-testid="get-instant-offer-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Calculating...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Get My Instant Offer
                      <ArrowRight className="w-5 h-5" />
                    </span>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/confirm-vehicle')}
                  className={`flex-1 ${fieldHeight} btn-secondary`}
                  data-testid="back-btn"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Back
                </Button>
              </div>
            </form>
          </div>
        </div>
      </main>
      {!compact && <Footer minimal />}
    </div>
  );
};

export default MileageZipScreen;
